import { TimeSeriesStrategyParams } from 'widgets/fillsGraph/fillsGraphStrategyParamsData';
import React, { useContext } from 'react';
import { useHorizontalZoomContext } from 'utils/components/Zoom/ZoomContext';
import AppContext from 'app/AppContext';
import XAxisRevisionLabel from '../axisLabel/XAxisRevisionLabel';
import XAxisLabel from '../axisLabel/XAxisLabel';
import { useFillsGraphConfigurationSelector } from 'widgets/fillsGraph/store/configuration/selectors';

type XAxisCommon = { left: number; boxLeft?: number };
interface XAxisRevisionLabelItem extends XAxisCommon {
    strategyParam: TimeSeriesStrategyParams;
}
interface XAxisTimeLabelItem extends XAxisCommon {
    label: string;
    className: string;
    time: number;
}
type XAxisLabelItem = XAxisRevisionLabelItem | XAxisTimeLabelItem;

function isXAxisRevisionLabel(t: XAxisLabelItem): t is XAxisRevisionLabelItem {
    return !!(t as XAxisRevisionLabelItem).strategyParam;
}

function isXAxisArrivalLabel(t: XAxisLabelItem): t is XAxisTimeLabelItem {
    return !isXAxisRevisionLabel(t) && t.className === 'arrival-label';
}

function isXAxisEndLabel(t: XAxisLabelItem): t is XAxisTimeLabelItem {
    return !isXAxisRevisionLabel(t) && t.className === 'end-label';
}

export interface OrderFillsChartXAxisLabelsProps {
    strategyParameterData: TimeSeriesStrategyParams[];
    arrivalTimestamp: number;
    endTimestamp?: number;
    plotAreaHeight: number;
}

export function OrderFillsChartXAxisLabels(
    props: OrderFillsChartXAxisLabelsProps
) {
    const {
        strategyParameterData,
        arrivalTimestamp,
        endTimestamp,
        plotAreaHeight,
    } = props;
    const { timeFormat } = useContext(AppContext);

    const { intervalToWidth, startX: startTime } = useHorizontalZoomContext();
    const {
        OrderFills: {
            displayAlgorithmParametersRevisions,
            hideArrivalAndAndLabels,
        },
    } = useFillsGraphConfigurationSelector();

    const labels: XAxisLabelItem[] = [];

    if (!hideArrivalAndAndLabels && arrivalTimestamp) {
        labels.push({
            left: intervalToWidth(arrivalTimestamp - startTime),
            className: 'arrival-label',
            label: `Arrival ${timeFormat.toTimeMinutes(arrivalTimestamp)}`,
            time: arrivalTimestamp,
        });
    }

    if (
        displayAlgorithmParametersRevisions &&
        strategyParameterData.length > 1
    ) {
        labels.push(
            ...strategyParameterData.map((x) => ({
                left: intervalToWidth(x.time - startTime),
                strategyParam: x,
            }))
        );
    }

    if (!hideArrivalAndAndLabels && endTimestamp) {
        labels.push({
            left: intervalToWidth(endTimestamp - startTime),
            className: 'end-label',
            label: `End ${timeFormat.toTimeMinutes(endTimestamp)}`,
            time: endTimestamp,
        });
    }

    const visibleLabels = labels.filter((l) => l.left >= 0).sort(sortLabels);
    setBoxMarginsForOverlap(visibleLabels);

    return (
        <>
            {visibleLabels.map((l) => {
                if (isXAxisRevisionLabel(l)) {
                    return (
                        <XAxisRevisionLabel
                            key={l.strategyParam.revisionIndex}
                            top={plotAreaHeight}
                            left={l.left}
                            revisionIndex={l.strategyParam.revisionIndex}
                            boxLeft={l.boxLeft}
                            time={l.strategyParam.time}
                        />
                    );
                }
                return (
                    <XAxisLabel
                        key={l.className}
                        label={l.label}
                        className={l.className}
                        top={plotAreaHeight}
                        left={l.left}
                        boxLeft={l.boxLeft}
                        time={l.time}
                    />
                );
            })}
        </>
    );
}

function setBoxMarginsForOverlap(labels: XAxisLabelItem[]) {
    for (let i = 0; i < labels.length - 1; i++) {
        const current = labels[i];
        const next = labels[i + 1];
        const currentBoxLeftPos = current.left + (current.boxLeft ?? 0);
        if (currentBoxLeftPos + 13 > next.left) {
            next.boxLeft = currentBoxLeftPos + 13 - next.left;
        }
    }
}

function sortLabels(a: XAxisLabelItem, b: XAxisLabelItem) {
    if (a.left !== b.left) {
        return a.left > b.left ? 1 : -1;
    }
    if (isXAxisArrivalLabel(a)) {
        return -1;
    }
    if (isXAxisEndLabel(a)) {
        return 1;
    }
    if (isXAxisRevisionLabel(a) && isXAxisRevisionLabel(b)) {
        return a.strategyParam.revisionIndex > b.strategyParam.revisionIndex
            ? 1
            : -1;
    }
    return 0;
}
